<script setup lang="ts" generic="T extends NavPosition">
import NavIcon from './NavIcon.vue';
import { type NavbarIcon, type NavPosition } from './types';

defineProps<{
    position: T;
    title: string;
    subtitle?: string;
    to?: string;
    href?: string;
    prependIcon?: T extends 'header' ? NavbarIcon : never;
    appendIcon?: NavbarIcon;
}>();

defineOptions({
    inheritAttrs: false,
});

const attrs = useAttrs();
const hasAction = computed(() => !!attrs.onClick);
const itemClicked = (ev: MouseEvent) => (attrs as any).onClick?.(ev);
</script>

<template>
    <li
        class="sub-item"
        :class="[attrs.class, position]"
        :style="attrs.style as any"
    >
        <div v-if="hasAction" class="sub-body" @click="itemClicked">
            <div class="main">
                <NavIcon
                    v-if="prependIcon && position === 'header'"
                    :icon="prependIcon as NavbarIcon"
                    default-color="var(--gray-800)"
                    class="mr-icon prepend"
                />
                <div class="sub-title">
                    {{ title }}
                </div>
                <NavIcon
                    v-if="appendIcon"
                    :icon="appendIcon"
                    default-color="var(--gray-800)"
                    class="mr-icon append"
                />
            </div>
            <span v-if="subtitle" class="sub-subtitle">
                {{ subtitle }}
            </span>
        </div>
        <nuxt-link v-else-if="to" :to class="sub-body">
            <div class="main">
                <NavIcon
                    v-if="prependIcon && position === 'header'"
                    :icon="prependIcon as NavbarIcon"
                    default-color="var(--gray-800)"
                    class="mr-icon prepend"
                />
                <div class="sub-title">
                    {{ title }}
                </div>
                <NavIcon
                    v-if="appendIcon"
                    :icon="appendIcon"
                    default-color="var(--gray-800)"
                    class="mr-icon append"
                />
            </div>
            <span v-if="subtitle" class="sub-subtitle">
                {{ subtitle }}
            </span>
        </nuxt-link>
        <a v-else-if="href" class="sub-body" :href>
            <div class="main">
                <NavIcon
                    v-if="prependIcon && position === 'header'"
                    :icon="prependIcon as NavbarIcon"
                    default-color="var(--gray-800)"
                    class="mr-icon prepend"
                />
                <div class="sub-title">
                    {{ title }}
                </div>
                <NavIcon
                    v-if="appendIcon"
                    :icon="appendIcon"
                    default-color="var(--gray-800)"
                    class="mr-icon append"
                />
            </div>
            <span v-if="subtitle" class="sub-subtitle">
                {{ subtitle }}
            </span>
        </a>
    </li>
</template>

<style scoped lang="scss">
.sidebar.sub-item {
    @include tg-m($w5);
    border: 0;
    padding: 0;
    .selected {
        .main {
            .sub-title {
                color: usePalette($p-blue, 400) !important;
            }
            :deep(.mr-icon-svg) {
                color: usePalette($p-blue, 400) !important;
            }
        }
    }
    .sub-body {
        color: black;
        &:hover {
            text-decoration: none;
        }
        &.router-link-exact-active {
            .sub-title {
                // color: usePalette() !important;
                color: usePalette($p-blue, 400) !important;
            }
            :deep(.mr-icon-svg) {
                color: usePalette($p-blue, 400) !important;
            }
        }
        .main {
            display: flex;
        }
    }
}

.header.sub-item {
    display: flex;
    flex-direction: column;
    padding: 0;
    cursor: pointer;
    &:hover {
        background-color: usePalette($p-blue, $p-100);
    }
    &:first-child {
        border-top-right-radius: 8px;
        border-top-left-radius: 8px;
    }
    &:last-child {
        border-bottom-right-radius: 8px;
        border-bottom-left-radius: 8px;
    }
    .sub-body {
        &:hover {
            text-decoration: none;
        }
        .main {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 10px 12px;
            height: 100%;
            text-decoration: none;
            .prepend,
            .append {
                margin: 0;
            }
            .sub-title {
                @include tg-m($w5);
            }
        }
    }
}
</style>
